import { TrelloLabel, LabelSuggestion, TaskWithLabels } from '../types/labels';
import { supabase } from './supabase';

export class AILabelAnalyzer {
  /**
   * Analyzes a single task for label suggestions
   */
  static async analyzeTaskForLabels(
    task: { title: string; description: string },
    availableLabels: TrelloLabel[]
  ): Promise<LabelSuggestion> {
    try {
      // Route through Supabase Edge Function instead of direct OpenAI call
      const { data, error } = await supabase.functions.invoke('ai-label-analysis', {
        body: {
          task,
          availableLabels
        }
      });

      if (error) {
        console.error('AI label analysis error:', error);
        return this.getFallbackSuggestion(task, availableLabels);
      }

      return data.suggestion || this.getFallbackSuggestion(task, availableLabels);
    } catch (error) {
      console.error('Error analyzing task for labels:', error);
      return this.getFallbackSuggestion(task, availableLabels);
    }
  }

  /**
   * Analyzes multiple tasks for label suggestions in batch
   */
  static async analyzeBatchTasksForLabels(
    tasks: { id: string; title: string; description: string }[],
    availableLabels: TrelloLabel[]
  ): Promise<LabelSuggestion[]> {
    try {
      // Route through Supabase Edge Function for batch analysis
      const { data, error } = await supabase.functions.invoke('ai-label-analysis', {
        body: {
          tasks,
          availableLabels,
          batch: true
        }
      });

      if (error) {
        console.error('AI batch label analysis error:', error);
        return tasks.map(task => this.getFallbackSuggestion(task, availableLabels));
      }

      return data.suggestions || tasks.map(task => this.getFallbackSuggestion(task, availableLabels));
    } catch (error) {
      console.error('Error analyzing tasks for labels:', error);
      return tasks.map(task => this.getFallbackSuggestion(task, availableLabels));
    }
  }

  /**
   * Get suggested color for a new label based on its name and purpose
   */
  static suggestLabelColor(labelName: string): string {
    const colorMap: Record<string, string> = {
      // Priority/Urgency
      'high': 'red',
      'urgent': 'red',
      'critical': 'red',
      'low': 'green',
      'minor': 'green',
      
      // Status
      'bug': 'red',
      'feature': 'blue',
      'enhancement': 'purple',
      'documentation': 'sky',
      'testing': 'lime',
      
      // Department
      'engineering': 'blue',
      'design': 'purple',
      'marketing': 'pink',
      'sales': 'orange',
      'product': 'yellow',
      
      // Technology
      'frontend': 'blue',
      'backend': 'black',
      'mobile': 'purple',
      'api': 'sky',
      'database': 'black'
    };

    const lowercaseName = labelName.toLowerCase();
    
    // Check for exact matches first
    for (const [keyword, color] of Object.entries(colorMap)) {
      if (lowercaseName.includes(keyword)) {
        return color;
      }
    }

    // Default colors based on common patterns
    if (lowercaseName.includes('important') || lowercaseName.includes('priority')) {
      return 'orange';
    }
    if (lowercaseName.includes('review') || lowercaseName.includes('meeting')) {
      return 'yellow';
    }
    if (lowercaseName.includes('done') || lowercaseName.includes('complete')) {
      return 'green';
    }

    // Default to blue for unknown labels
    return 'blue';
  }

  private static validatePriority(priority: string): 'High' | 'Medium' | 'Low' {
    const validPriorities = ['High', 'Medium', 'Low'];
    return validPriorities.includes(priority) ? priority as 'High' | 'Medium' | 'Low' : 'Medium';
  }

  private static validateExistingLabels(suggested: string[], available: string[]): string[] {
    if (!Array.isArray(suggested)) return [];
    
    return suggested
      .filter(label => available.includes(label))
      .slice(0, 3); // Maximum 3 labels
  }

  private static getFallbackSuggestion(task: { title: string; description: string }, availableLabels: TrelloLabel[]): LabelSuggestion {
    const existingLabelNames = availableLabels.map(label => label.name);
    const priority = this.validatePriority('Medium'); // Default to Medium
    const existingLabels = this.validateExistingLabels([], existingLabelNames);
    const confidence = 0.1; // Low confidence for fallback

    return {
      priority,
      existingLabels,
      confidence
    };
  }
} 